import { z } from "zod";
import { apiGet, apiPost, apiPut, apiDelete } from "../lib/api";

export const CampaignStateSchema = z.enum([
  "draft", "active", "running", "paused", "completed", "cancelled",
]);
export type CampaignState = z.infer<typeof CampaignStateSchema>;


export const CampaignListItemSchema = z.object({
  campaign_id: z.string(),
  display_name: z.string(),
  state: CampaignStateSchema,
  engagement_count: z.number(),
  targets_total: z.number(),
  targets_completed: z.number(),
  targets_failed: z.number(),
  created_at: z.string(),
});
export type CampaignListItem = z.infer<typeof CampaignListItemSchema>;

export const CampaignDetailSchema = z.object({
  campaign_id: z.string(),
  customer_id: z.string(),
  display_name: z.string(),
  description: z.string().nullable(),
  state: CampaignStateSchema,
  max_attempts_per_target: z.number(),
  created_at: z.string(),
  created_by: z.string(),
  activated_at: z.string().nullable(),
  started_at: z.string().nullable(),
  completed_at: z.string().nullable(),
  cancelled_at: z.string().nullable(),
  engagement_count: z.number(),
  targets_total: z.number(),
  targets_completed: z.number(),
  targets_failed: z.number(),
  target_group_id: z.string().nullable().optional(),
  roe_signed_by: z.string().nullable().default(null),
  roe_signed_at: z.string().nullable().default(null),
  window_start: z.string().nullable().default(null),
  window_end: z.string().nullable().default(null),
  window_business_hours_only: z.boolean().nullable().default(null),
  window_timezone: z.string().nullable().default(null),
  window_business_hours_start: z.string().nullable().default(null),
  window_business_hours_end: z.string().nullable().default(null),
  window_business_days: z.array(z.number()).nullable().default(null),
});
export type CampaignDetail = z.infer<typeof CampaignDetailSchema>;

// ─── Campaign Engagement schemas ───────────────────────────────────────────────

export const CampaignEngagementStateSchema = z.enum([
  "scheduled", "running", "paused", "completed", "cancelled", "failed",
]);
export type CampaignEngagementState = z.infer<typeof CampaignEngagementStateSchema>;

export const CampaignEngagementItemSchema = z.object({
  campaign_id: z.string(),
  sort_order: z.number(),
  engagement_id: z.string(),
  engagement_display_name: z.string(),
  scheduled_start_at: z.string().nullable(),
  scheduled_end_at: z.string().nullable(),
  active_daily_start: z.string().nullable(),
  active_daily_end: z.string().nullable(),
  stagger_pattern: z.string(),
  stagger_interval_seconds: z.number().nullable(),
  stagger_jitter_seconds: z.number(),
  state: CampaignEngagementStateSchema,
  active_call_id: z.string().nullable(),
  active_target_sort_order: z.number().nullable(),
  targets_completed: z.number(),
  targets_failed: z.number(),
});
export type CampaignEngagementItem = z.infer<typeof CampaignEngagementItemSchema>;

export const CampaignEngagementListResponseSchema = z.object({
  items: z.array(CampaignEngagementItemSchema),
  total: z.number(),
});

export const EngagementActiveCallSchema = z.object({
  call_id: z.string(),
  room_name: z.string(),
  livekit_url: z.string(),
  operator_access_token: z.string(),
  target_display_name: z.string(),
  target_phone_e164: z.string(),
  target_sort_order: z.number(),
  engagement_sort_order: z.number(),
});
export type EngagementActiveCall = z.infer<typeof EngagementActiveCallSchema>;


// Field names must match console-api's CampaignEngagementTargetItem exactly
// (schemas/campaign.py:194). This schema previously declared `sort_order` and
// `verdict` while the server emits `target_sort_order` and `call_verdict`, so the
// zod parse threw on ANY non-empty target list — a running campaign with targets
// rendered "No targets assigned yet." forever. Server names are pinned by
// console-api tests, so the client was the wrong side.
export const CampaignEngagementTargetItemSchema = z.object({
  campaign_id: z.string(),
  engagement_sort_order: z.number(),
  target_sort_order: z.number(),
  target_id: z.string(),
  target_phone_e164: z.string(),
  target_display_name: z.string(),
  status: z.string(),
  call_id: z.string().nullable(),
  call_verdict: z.string().nullish(),
  outcome_note: z.string().nullish(),
  call_duration_seconds: z.number().nullable(),
  termination_reason: z.string().nullable(),
  attempts: z.number(),
  last_attempted_at: z.string().nullable(),
});
export type CampaignEngagementTargetItem = z.infer<typeof CampaignEngagementTargetItemSchema>;

export const CampaignEngagementTargetListResponseSchema = z.object({
  items: z.array(CampaignEngagementTargetItemSchema),
  total: z.number(),
});

export const CampaignTargetItemSchema = z.object({
  campaign_id: z.string(),
  sort_order: z.number(),
  target_id: z.string(),
  target_phone_e164: z.string(),
  target_display_name: z.string(),
});
export type CampaignTargetItem = z.infer<typeof CampaignTargetItemSchema>;

export const CampaignListResponseSchema = z.object({
  items: z.array(CampaignListItemSchema),
  total: z.number(),
});

export const CampaignDetailResponseSchema = z.object({
  record: CampaignDetailSchema,
});

export const CampaignTargetListResponseSchema = z.object({
  items: z.array(CampaignTargetItemSchema),
  total: z.number(),
});

export const ActiveCallResponseSchema = z.object({
  call_id: z.string(),
  room_name: z.string(),
  livekit_url: z.string(),
  operator_access_token: z.string(),
  target_display_name: z.string(),
  target_phone_e164: z.string(),
  sort_order: z.number(),
});
export type ActiveCallInfo = z.infer<typeof ActiveCallResponseSchema>;

export function fetchCampaigns() {
  return apiGet("/api/campaigns", CampaignListResponseSchema);
}

export function fetchCampaign(id: string) {
  return apiGet(`/api/campaigns/${id}`, CampaignDetailResponseSchema);
}

export function createCampaign(body: {
  customer_id: string;
  display_name: string;
  description?: string;
}) {
  return apiPost("/api/campaigns", CampaignDetailResponseSchema, body);
}

export function updateCampaign(id: string, body: {
  display_name?: string;
  description?: string;
  target_group_id?: string | null;
  roe?: { sha256: string; version: number; signed_by: string; signed_at: string };
  window?: {
    start: string; end: string;
    business_hours_only?: boolean;
    business_hours_timezone?: string;
    business_hours_start?: string;
    business_hours_end?: string;
    business_days?: number[];
  };
}) {
  return apiPut(`/api/campaigns/${id}`, CampaignDetailResponseSchema, body);
}

export function deleteCampaign(id: string) {
  return apiDelete(`/api/campaigns/${id}`);
}

export function transitionCampaign(
  id: string,
  body: { target_state: string; halt_active_call?: boolean },
) {
  return apiPost(`/api/campaigns/${id}/transition`, CampaignDetailResponseSchema, body);
}

export function fetchCampaignTargets(id: string) {
  return apiGet(`/api/campaigns/${id}/targets`, CampaignTargetListResponseSchema);
}

export function addCampaignTargets(id: string, target_ids: string[]) {
  return apiPost(`/api/campaigns/${id}/targets`, CampaignTargetListResponseSchema, { target_ids });
}

export function skipCampaignTarget(id: string, sortOrder: number) {
  return apiPost(
    `/api/campaigns/${id}/targets/${sortOrder}/skip`,
    CampaignTargetListResponseSchema,
  );
}

export function fetchActiveCall(id: string) {
  return apiGet(`/api/campaigns/${id}/active-call`, ActiveCallResponseSchema);
}

// ─── Campaign engagement fetchers ───────────────────────────────────────────────

export function fetchCampaignEngagements(id: string) {
  return apiGet(`/api/campaigns/${id}/engagements`, CampaignEngagementListResponseSchema);
}

export function addCampaignEngagement(
  id: string,
  body: {
    engagement_id: string;
    scheduled_start_at?: string;
    scheduled_end_at?: string;
    active_daily_start?: string;
    active_daily_end?: string;
    stagger_pattern: string;
    stagger_interval_seconds?: number;
    stagger_jitter_seconds: number;
  },
) {
  return apiPost(`/api/campaigns/${id}/engagements`, CampaignEngagementListResponseSchema, body);
}

export function updateCampaignEngagement(
  id: string,
  sortOrder: number,
  body: {
    scheduled_start_at?: string;
    scheduled_end_at?: string;
    active_daily_start?: string;
    active_daily_end?: string;
    stagger_pattern?: string;
    stagger_interval_seconds?: number;
    stagger_jitter_seconds?: number;
  },
) {
  return apiPut(
    `/api/campaigns/${id}/engagements/${sortOrder}`,
    CampaignEngagementListResponseSchema,
    body,
  );
}

export function deleteCampaignEngagement(id: string, sortOrder: number) {
  return apiDelete(`/api/campaigns/${id}/engagements/${sortOrder}`);
}

export function fetchEngagementTargets(id: string, sortOrder: number) {
  return apiGet(
    `/api/campaigns/${id}/engagements/${sortOrder}/targets`,
    CampaignEngagementTargetListResponseSchema,
  );
}

export function fetchEngagementActiveCall(id: string, sortOrder: number) {
  return apiGet(
    `/api/campaigns/${id}/engagements/${sortOrder}/active-call`,
    EngagementActiveCallSchema,
  );
}

export function haltCampaignEngagement(id: string, sortOrder: number) {
  return apiPost(
    `/api/campaigns/${id}/engagements/${sortOrder}/halt`,
    CampaignEngagementListResponseSchema,
  );
}

export const STATE_BADGE_COLOR: Record<CampaignState, string> = {
  draft: "var(--text-muted)",
  active: "var(--info)",
  running: "var(--success)",
  paused: "var(--warning)",
  completed: "var(--text-secondary)",
  cancelled: "var(--danger)",
};

export const ENGAGEMENT_STATE_COLOR: Record<string, string> = {
  scheduled: "var(--text-muted)",
  running: "var(--success)",
  paused: "var(--warning)",
  completed: "var(--text-secondary)",
  cancelled: "var(--danger)",
  failed: "var(--danger)",
};

